import useUrlState from '@ahooksjs/use-url-state';
import { ProFormInstance } from '@ant-design/pro-form';
import { useLocalStorageState } from 'ahooks';
import React, {
  useEffect, useRef,
} from 'react';
import * as uuid from 'uuid';
import { useAppConfig } from '@/config/context';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import {
  CHARACTER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { stringFilterTips } from '@/presentation/components/issue-list/components/tips';
import { FilterEntry } from '@/presentation/components/form/types';
import {
  ITdsFormRef,
  TdsFilterType,
  TdsForm,
  TdsFormItem,
  TdsFormItemType,
} from '@/presentation/components/tds-form';
import { DEFAULT_EVENT_TIME_RANGE } from '@/presentation/components/tds-form/config';
import { COMMON_TIME_TYPE_OPTIONS, ISSUE_TIME_TYPE } from '@/presentation/components/tds-form/tds-date-cascader/config';
import {
  dateCascaderConverter,
  dateCascaderTransformer,
} from '@/presentation/components/tds-form/utils';
import { useTdsForm } from '@/presentation/hooks/form';
import { KUIKLY_ERROR_LEVEL_OPTIONS, KuiklyFieldFilterKey, KuiklyFieldKey } from '@/presentation/pages/kuikly/types';
import {
  CommonFieldKey,
  StandardCommonFieldKey,
  Zone,
} from '@/proto/field';
import { IFilter } from '@/proto/metric';
import { genStandardToken, parseStandardToken } from '@/proto/token';

export const KuiklyErrorForm = (props: {
  onTokenChange?: (token: string) => void;
  name?: string;
  formRef?: React.MutableRefObject<ProFormInstance<any>>;
  entry?: FilterEntry;
}) => {
  const { onTokenChange, entry } = props;
  const ref = useRef<any>();
  const formRef = props.formRef ?? ref;
  const tableRef = useRef<ITdsFormRef>(null);
  const [state, setState] = useUrlState({
    token: '',
  });
  const [timeRange, setTimeRange] = useLocalStorageState(
    'kuikly-error-list',
    {
      defaultValue: DEFAULT_EVENT_TIME_RANGE,
    },
  );
  const config = useAppConfig();
  const [, useTdsFormContext] = useTdsForm;

  const { updateFormContext } = useTdsFormContext();

  const items: TdsFormItem[] = [
    {
      type: TdsFormItemType.DateCascader,
      label: '时间范围',
      name: StandardCommonFieldKey.TimeRange,
      width: 556,
      deleteable: false,
      convertValue: dateCascaderConverter,
      transform: dateCascaderTransformer,
      fieldProps: {
        maxLimitedDays: 90,
        timeWindowOptions: ISSUE_TIME_TYPE,
        timeTypeOptions: COMMON_TIME_TYPE_OPTIONS,
        showTimeType: false,
        showTimeWindow: entry !== FilterEntry.KuiklyKotlinAnalysis &&  entry !== FilterEntry.KuiklyKotlinDetail,
        rangePickerProps: {
          style: {
            width: 320,
          },
        },
      },
      inFilter: true,
      filterType: TdsFilterType.DateCascader,
    },
    {
      type: TdsFormItemType.FieldSelector,
      label: '错误类型',
      name: KuiklyFieldKey.Level,
      fieldProps: {
        mode: 'tags',
        options: KUIKLY_ERROR_LEVEL_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.Level,
      filterType: TdsFilterType.EnumString,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '错误内容',
      name: KuiklyFieldKey.ErrorMsg,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.ErrorMsg,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'APP版本',
      name: KuiklyFieldKey.AppVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.AppVersion,
          fieldScope: Zone.RumJs,
          dropdownMatchSelectWidth: 300,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.AppVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Shiply制品版本',
      name: KuiklyFieldKey.ProductVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.ProductVersion,
          fieldScope: Zone.RumJs,
          dropdownMatchSelectWidth: 300,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.ProductVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '平台',
      name: KuiklyFieldKey.Platform,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.Platform,
          fieldScope: Zone.RumJs,
          forceRemote: true,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.Platform,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '运营商',
      name: KuiklyFieldKey.Operator,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.Operator,
          fieldScope: Zone.RumJs,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.Operator,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '系统版本',
      name: KuiklyFieldKey.Os,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.Os,
          fieldScope: Zone.RumJs,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.Os,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '厂商',
      name: KuiklyFieldKey.Brand,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.Brand,
          fieldScope: Zone.RumJs,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.Brand,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '机型',
      name: KuiklyFieldKey.Model,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.Model,
          fieldScope: Zone.RumJs,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.Model,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '用户ID',
      name: StandardCommonFieldKey.AccountId,
      filterName: CommonFieldKey.AccountId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      batchEditMode: true,
      batchEditTips: stringFilterTips,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '实验ID',
      name: KuiklyFieldKey.AbTags,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.AbTags,
          fieldScope: Zone.RumJs,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.AbTags,
      filterType: TdsFilterType.StringArray,
      hidden: !config.enableShiplyAbTestTag,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '页面名称',
      name: KuiklyFieldKey.From,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.From,
          fieldScope: Zone.RumJs,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.From,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '框架版本',
      name: KuiklyFieldKey.FrameworkVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.FrameworkVersion,
          fieldScope: Zone.RumApi,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.FrameworkVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'SDK版本',
      name: KuiklyFieldKey.SdkVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: KuiklyFieldFilterKey.SdkVersion,
          fieldScope: Zone.RumApi,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: KuiklyFieldFilterKey.SdkVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '设备ID',
      name: StandardCommonFieldKey.Qimei,
      filterName: CommonFieldKey.Qimei,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '用户ID',
      name: StandardCommonFieldKey.AccountId,
      filterName: CommonFieldKey.AccountId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
  ];

  const initToken = async () => {
    if (!state.token) {
      formRef.current?.submit();
    } else {
      const fieldsValue = await parseStandardToken(state.token);
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(items, fieldsValue)?.then?.(() => {
        formRef?.current?.setFieldsValue?.(fieldsValue);
      });
      updateFormContext?.(draft => ({
        ...draft,
        fieldsValue,
        fieldsFilter: fieldsValue?.filters ?? [],
        token: state.token,
        uuidFetch: uuid.v4(),
      }));
      onTokenChange?.(state.token);
    }
  };

  const onFinish = async (values: Record<string, any>, filters: IFilter[]) => {
    // 缓存选择过的时间范围
    setTimeRange(values?.[StandardCommonFieldKey.TimeRange] ?? DEFAULT_EVENT_TIME_RANGE);
    const token = await genStandardToken({
      ...values,
      filters,
      fetchUUID: uuid.v4(),
    });
    updateFormContext(draft => ({
      ...draft,
      token,
      fieldsValue: values,
      fieldsFilter: filters,
      uuidFetch: uuid.v4(),
    }));
    setState({
      token,
    });
    onTokenChange?.(state.token);
  };

  useEffect(() => {
    initToken();
  }, []);

  return <TdsForm
    ref={tableRef}
    pageId={PAGE_ID.kuikly.error}
    formRef={formRef}
    onFinish={onFinish}
    items={items}
    name={props.name || 'kuikly-error'}
    initialValues={{
      [StandardCommonFieldKey.TimeRange]: timeRange,
    }}
  />;
};
